import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { ApiError } from "@hostdock/sdk";
import type { CustomerRecord, CustomerStatus } from "@hostdock/types";
import {
  ActionMenu, Badge, Button, ConfirmDialog, DataTable, Dialog, ErrorState, Field, FormError, formatDateTime, formatRelative, Input,
  KeyValue, OneTimeSecret, PageHeader, Panel, Skeleton, StatusBadge, toast,
} from "@hostdock/ui";
import { api } from "../api";
import { actionLabel, ActorCell } from "../lib/audit-labels";
import { RequirePermission } from "../lib/guard";
import { useSession } from "../session";
import { CustomerSubscriptions } from "./customer-subscriptions";

type Pending = null | "edit" | "suspend" | "activate" | "close" | "reset-password" | "reset-mfa" | "revoke";

export function CustomerDetailPage() {
  return (
    <RequirePermission anyOf={["CUSTOMER_VIEW"]}>
      <CustomerDetail />
    </RequirePermission>
  );
}

function CustomerDetail() {
  const { id = "" } = useParams();
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["customer", id], queryFn: () => api.customers.get(id) });
  const [pending, setPending] = useState<Pending>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["customer", id] });
    void qc.invalidateQueries({ queryKey: ["customers"] });
    void qc.invalidateQueries({ queryKey: ["customer-audit", id] });
  };
  const done = (msg: string) => {
    toast.success(msg);
    setPending(null);
    refresh();
  };

  const setStatus = useMutation({ meta: { inlineError: true },
    mutationFn: (v: { status: CustomerStatus; reason: string }) => api.customers.setStatus(id, v),
    onSuccess: (c) => done(`Customer is now ${c.status.toLowerCase()}`),
  });
  const resetPassword = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.customers.resetPassword(id),
    onSuccess: (r) => {
      setPending(null);
      setTempPassword(r.temporaryPassword);
      refresh();
    },
  });
  const resetMfa = useMutation({ meta: { inlineError: true }, mutationFn: (reason: string) => api.customers.resetMfa(id, reason), onSuccess: () => done("Two-factor authentication reset") });
  const revoke = useMutation({ meta: { inlineError: true }, mutationFn: () => api.customers.revokeSessions(id), onSuccess: (r) => done(`Signed out ${r.revoked} session${r.revoked === 1 ? "" : "s"}`) });

  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const c = q.data;
  const manage = can("CUSTOMER_MANAGE") && c && c.status !== "CLOSED";

  return (
    <>
      <PageHeader
        title={c ? c.displayName : <Skeleton className="h-6 w-48" />}
        description={c?.companyName ?? undefined}
        meta={c && <StatusBadge status={c.status} />}
        actions={
          manage && (
            <>
              <Button onClick={() => setPending("edit")}>Edit</Button>
              {c.status === "ACTIVE" ? (
                <Button variant="danger-ghost" onClick={() => setPending("suspend")}>Suspend</Button>
              ) : (
                <Button variant="primary" onClick={() => setPending("activate")}>Reactivate</Button>
              )}
              <ActionMenu
                actions={[
                  { label: "Reset password", onSelect: () => setPending("reset-password") },
                  { label: "Reset two-factor", onSelect: () => setPending("reset-mfa"), hidden: !c.owner.mfaEnabled },
                  { label: "Sign out all sessions", onSelect: () => setPending("revoke") },
                  { label: "Close account", onSelect: () => setPending("close"), danger: true },
                ]}
                trigger={<Button aria-label="More actions">More</Button>}
              />
            </>
          )
        }
      />
      <div className="grid gap-5 lg:grid-cols-[1fr_1fr]">
        <Panel title="Account">
          {c ? (
            <KeyValue
              items={[
                { label: "Account name", value: c.displayName },
                { label: "Company", value: c.companyName ?? "—" },
                { label: "Phone", value: c.phone ?? "—" },
                { label: "Customer ID", value: <span className="font-mono text-[12.5px]">{c.id}</span> },
                { label: "Billing client", value: <BillingLink customer={c} editable={can("CUSTOMER_MANAGE") && can("BILLING_MANAGE")} /> },
                { label: "Created", value: formatDateTime(c.createdAt) },
              ]}
            />
          ) : (
            <Skeleton className="h-32 w-full" />
          )}
        </Panel>
        <Panel title="Owner login">
          {c ? (
            <KeyValue
              items={[
                { label: "Name", value: c.owner.name },
                { label: "Email", value: c.owner.email },
                { label: "Two-factor", value: <Badge tone={c.owner.mfaEnabled ? "success" : "neutral"}>{c.owner.mfaEnabled ? "Enabled" : "Not enabled"}</Badge> },
                { label: "Last sign-in", value: c.owner.lastLoginAt ? formatDateTime(c.owner.lastLoginAt) : "Never" },
              ]}
            />
          ) : (
            <Skeleton className="h-32 w-full" />
          )}
        </Panel>
      </div>
      {c && <CustomerSubscriptions customerId={c.id} customerActive={c.status === "ACTIVE"} />}
      {can("AUDIT_VIEW") && <CustomerAudit customerId={id} />}

      {c && <EditCustomerDialog key={c.updatedAt} customer={c} open={pending === "edit"} onOpenChange={(o) => setPending(o ? "edit" : null)} onSaved={() => done("Customer updated")} />}
      <ConfirmDialog
        open={pending === "suspend"}
        onOpenChange={(o) => !o && setPending(null)}
        title="Suspend customer?"
        description="The owner is signed out immediately and cannot sign in until the account is reactivated."
        confirmLabel="Suspend"
        requireReason
        loading={setStatus.isPending}
        error={setStatus.error}
        onConfirm={(reason) => setStatus.mutate({ status: "SUSPENDED", reason })}
      />
      <ConfirmDialog
        open={pending === "activate"}
        onOpenChange={(o) => !o && setPending(null)}
        title="Reactivate customer?"
        tone="primary"
        confirmLabel="Reactivate"
        requireReason
        loading={setStatus.isPending}
        error={setStatus.error}
        onConfirm={(reason) => setStatus.mutate({ status: "ACTIVE", reason })}
      />
      <ConfirmDialog
        open={pending === "close"}
        onOpenChange={(o) => !o && setPending(null)}
        title="Close this account permanently?"
        description="Closed accounts cannot be reopened or edited. The owner is signed out immediately."
        confirmLabel="Close account"
        confirmText={c?.displayName}
        requireReason
        loading={setStatus.isPending}
        error={setStatus.error}
        onConfirm={(reason) => setStatus.mutate({ status: "CLOSED", reason })}
      />
      <ConfirmDialog
        open={pending === "reset-password"}
        onOpenChange={(o) => !o && setPending(null)}
        title="Reset the owner's password?"
        description="A new temporary password is generated, all sessions are signed out and the owner must set a new password at next sign-in."
        confirmLabel="Reset password"
        loading={resetPassword.isPending}
        error={resetPassword.error}
        onConfirm={() => resetPassword.mutate()}
      />
      <ConfirmDialog
        open={pending === "reset-mfa"}
        onOpenChange={(o) => !o && setPending(null)}
        title="Reset two-factor authentication?"
        description="Only do this after verifying the customer's identity. Their authenticator and recovery codes stop working."
        confirmLabel="Reset two-factor"
        requireReason
        loading={resetMfa.isPending}
        error={resetMfa.error}
        onConfirm={(reason) => resetMfa.mutate(reason)}
      />
      <ConfirmDialog
        open={pending === "revoke"}
        onOpenChange={(o) => !o && setPending(null)}
        title="Sign out all sessions?"
        confirmLabel="Sign out"
        loading={revoke.isPending}
        error={revoke.error}
        onConfirm={() => revoke.mutate()}
      />
      <Dialog open={tempPassword !== null} onOpenChange={(o) => !o && setTempPassword(null)} title="Password reset" footer={<Button variant="primary" onClick={() => setTempPassword(null)}>Done</Button>}>
        {tempPassword && <OneTimeSecret label={`Temporary password for ${c?.owner.email ?? "the owner"}`} value={tempPassword} />}
      </Dialog>
    </>
  );
}

function EditCustomerDialog({ customer, open, onOpenChange, onSaved }: { customer: CustomerRecord; open: boolean; onOpenChange(o: boolean): void; onSaved(): void }) {
  const [form, setForm] = useState({ displayName: customer.displayName, companyName: customer.companyName ?? "", phone: customer.phone ?? "", ownerName: customer.owner.name });
  const save = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.customers.update(customer.id, { displayName: form.displayName, companyName: form.companyName || null, phone: form.phone || null, ownerName: form.ownerName }),
    onSuccess: onSaved,
  });
  const fe = save.error instanceof ApiError ? save.error.fieldErrors : {};
  const upd = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit customer"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save changes</Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Account name" error={fe.displayName}>{(p) => <Input {...p} value={form.displayName} onChange={upd("displayName")} />}</Field>
        <Field label="Company" error={fe.companyName}>{(p) => <Input {...p} value={form.companyName} onChange={upd("companyName")} />}</Field>
        <Field label="Owner full name" error={fe.ownerName}>{(p) => <Input {...p} value={form.ownerName} onChange={upd("ownerName")} />}</Field>
        <Field label="Phone" error={fe.phone}>{(p) => <Input {...p} value={form.phone} onChange={upd("phone")} />}</Field>
      </div>
      {save.error && !Object.keys(fe).length ? <div className="mt-4"><FormError error={save.error} /></div> : null}
    </Dialog>
  );
}

function CustomerAudit({ customerId }: { customerId: string }) {
  const q = useQuery({ queryKey: ["customer-audit", customerId], queryFn: () => api.audit.list({ customerId, pageSize: 20 }) });
  return (
    <Panel title="Account activity" className="mt-5" flush>
      <DataTable
        rows={q.data?.items}
        loading={q.isPending}
        error={q.error instanceof ApiError && q.error.isForbidden ? null : q.error}
        onRetry={() => void q.refetch()}
        rowKey={(r) => r.id}
        skeletonRows={4}
        columns={[
          { key: "when", header: "When", cell: (r) => <span className="whitespace-nowrap text-muted" title={formatDateTime(r.occurredAt)}>{formatRelative(r.occurredAt)}</span> },
          { key: "action", header: "Action", cell: (r) => actionLabel(r.action) },
          { key: "actor", header: "Actor", cell: (r) => <ActorCell entry={r} />, hideOnMobile: true },
          { key: "result", header: "Result", cell: (r) => <StatusBadge status={r.result} /> },
        ]}
      />
    </Panel>
  );
}

/** The WHMCS client this customer is linked to. Linking lets WHMCS provision for an existing customer. */
function BillingLink({ customer, editable }: { customer: CustomerRecord; editable: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(customer.billingClientId ?? "");
  const save = useMutation({
    mutationFn: () => api.billing.linkCustomer(customer.id, value.trim() || null),
    onSuccess: () => { toast.success(value.trim() ? "Billing client linked" : "Billing link removed"); setEditing(false); void qc.invalidateQueries({ queryKey: ["customer", customer.id] }); },
  });
  if (!editing) {
    return (
      <span className="flex items-center gap-2">
        <span className={customer.billingClientId ? "font-mono text-[12.5px]" : "text-muted"}>{customer.billingClientId ?? "Not linked"}</span>
        {editable && <Button size="sm" variant="ghost" onClick={() => { setValue(customer.billingClientId ?? ""); setEditing(true); }}>{customer.billingClientId ? "Change" : "Link"}</Button>}
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-1.5">
      <span className="flex items-center gap-2">
        <Input aria-label="WHMCS client ID" className="h-8 w-36 font-mono" value={value} maxLength={64} placeholder="WHMCS client ID" onChange={(e) => setValue(e.target.value)} />
        <Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
        <Button size="sm" variant="ghost" onClick={() => { setEditing(false); save.reset(); }}>Cancel</Button>
      </span>
      <FormError error={save.error} />
    </span>
  );
}
